/**
 * The firm-facing document sync audit — the outbound documents its PMS should hold and, per Vale's
 * own receipts, does not, plus the trigger that files a selected backlog.
 *
 * Provider-agnostic like every other route under `/customer-integrations/pms/:provider/…`, and
 * reachable in states where other PMS affordances are not: the audit is a **local read** over Vale's
 * sync records, so it answers correctly for a firm whose connection has lapsed and for one that has
 * switched automatic filing off. Those are the two states the feature exists for.
 *
 * Throws `DirectApiError` on failure — the `client.ts` convention for `directApiClient`-backed
 * helpers — with one deliberate exception: the trigger's `409` is a documented **outcome**, not an
 * error. See {@link triggerDocumentSyncAudit}.
 */

'use client';

import { directApiClient, DirectApiError } from '@/lib/api/direct-client';
import type {
  DocumentSyncAuditItem,
  DocumentSyncAuditResponse,
  DocumentSyncAuditSyncResult,
  PmsProvider
} from './types';

export async function getDocumentSyncAudit(
  provider: PmsProvider
): Promise<DocumentSyncAuditResponse> {
  return directApiClient.get<DocumentSyncAuditResponse>(
    `/customer-integrations/pms/${provider}/document-sync-audit`
  );
}

/**
 * File the selected items, and report what was **started** — not what was filed.
 *
 * The uploads outlive the request by design (correspondence through Temporal, files through a
 * detached task), so closing the dialog is not a cancel and there is no progress store. Progress is
 * observed by re-reading the audit and watching rows drop off as they gain receipts.
 *
 * **The `409` is returned, not thrown.** A run already in flight for this firm is the deliberate
 * "rejected, not queued" outcome, and its payload is what lets a reopened dialog attach to the live
 * run instead of erroring or starting a rival — so translating it into a thrown error would discard
 * the one thing it exists to carry. Every other status still throws.
 */
export async function triggerDocumentSyncAudit(
  provider: PmsProvider,
  items: Array<Pick<DocumentSyncAuditItem, 'itemId' | 'kind'>>
): Promise<DocumentSyncAuditSyncResult> {
  try {
    const response = await directApiClient.post<
      Omit<
        Extract<DocumentSyncAuditSyncResult, { outcome: 'dispatched' }>,
        'outcome'
      >
    >(`/customer-integrations/pms/${provider}/document-sync-audit/sync`, {
      items: items.map(({ itemId, kind }) => ({ itemId, kind }))
    });

    return { outcome: 'dispatched', ...response };
  } catch (error: unknown) {
    if (error instanceof DirectApiError && error.status === 409) {
      /*
        Both fields are optional on the wire rather than guaranteed. The backend reads the running
        lock back AFTER failing to acquire it, and the lock can be released in between — it rejects
        anyway (the caller wins the next acquire), so the 409 can legitimately arrive carrying no
        run to describe. Rendering "a run is in progress" with no start time is correct there;
        inventing one would not be.
      */
      return {
        outcome: 'already-running',
        runningSince: error.response?.runningSince ?? null,
        lockExpiresAt: error.response?.lockExpiresAt ?? null
      };
    }

    throw error;
  }
}
